// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type BrokerAccountResp,
  type AccountInfo,
  type TransactionInfo,
  type Broker,
} from '@shared/data-access/interfaces/api/brokerage/account';
import {
  type BrokerConfiguration,
  type BrokerCredentials,
  type BrokerCredentialsResp,
  type BrokerageCredentials,
} from '@shared/data-access/interfaces/api/brokerage/commons';
import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import {
  accountPayload,
  credentialsPayload,
} from '@shared/data-access/utils/payloads/brokerage';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const accountApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    account: build.query<
      BrokerAccountResp,
      { params: BrokerConfiguration; brokerCredentials: BrokerageCredentials }
    >({
      query: ({ params, brokerCredentials }) => ({
        url: '/v2/account/',
        method: 'POST',
        params: params,
        body: cleanPayload(brokerCredentials, accountPayload),
      }),
      providesTags: [{ type: 'Account', id: 'Info' }],
    }),

    accountAll: build.query<AccountInfo[], { params: BrokerConfiguration }>({
      query: ({ params }) => ({
        url: '/v2/account/all/',
        method: 'POST',
        params,
      }),
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: /* istanbul ignore next */ result =>
        result
          ? [
              ...result.map(({ account_id }) => ({
                type: 'Account' as const,
                id: account_id,
              })),
              { type: 'Account', id: 'List' },
              SharedTagTypes.CurrencyConversion,
            ]
          : [{ type: 'Account', id: 'List' }, SharedTagTypes.CurrencyConversion],
    }),

    accountTransaction: build.query<
      TransactionInfo[],
      { params: BrokerConfiguration; payload: BrokerageCredentials }
    >({
      query: ({ params, payload }) => ({
        url: '/v2/account/transaction/',
        method: 'POST',
        params,
        body: cleanPayload(payload, accountPayload),
      }),
      extraOptions: {},
      providesTags: [{ type: 'Account', id: 'Transaction' }],
    }),

    postBrokerCredentials: build.mutation<
      BrokerCredentialsResp,
      { brokerKey: Broker; brokerCredentials: BrokerCredentials }
    >({
      query: ({ brokerCredentials, brokerKey }) => ({
        url: '/v2/account/brokers/credentials/',
        method: 'POST',
        params: {
          broker_key: brokerKey,
        },
        body: cleanPayload(brokerCredentials, credentialsPayload),
      }),
      invalidatesTags: ['Account'],
    }),
  }),
});
